import { z } from 'zod';
import { DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT } from '../constants';
import { normalizeEthiopianPhone } from '../phone';
import { SharedError } from '../errors';

export const objectIdSchema = z.string().regex(/^[0-9a-f]{24}$/i, 'Invalid id');

/** `:id` route param. */
export const idParamSchema = z.object({ id: objectIdSchema });
export type IdParam = z.infer<typeof idParamSchema>;

/** Positive integer santim (safe integer). */
export const santimSchema = z.number().int().safe().positive();

/** Trimmed string with length bounds. */
export const text = (min: number, max: number) => z.string().trim().min(min).max(max);

export const httpUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .url()
  .refine((v) => /^https?:\/\//i.test(v), 'URL must start with http:// or https://');

export const isoDateSchema = z.string().datetime({ offset: true });

/** Both languages required and non-empty. */
export const localizedTextSchema = (max: number) =>
  z.object({ en: text(1, max), am: text(1, max) });

/** Both keys present; empty strings allowed (used for drafts before translation is finished). */
export const localizedDraftSchema = (max: number) =>
  z.object({ en: z.string().trim().max(max), am: z.string().trim().max(max) });

/** Ethiopian phone number accepted in any common format; output is normalized E.164. */
export const phoneSchema = z.string().transform((value, ctx) => {
  try {
    return normalizeEthiopianPhone(value);
  } catch (error) {
    if (error instanceof SharedError) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: error.message });
      return z.NEVER;
    }
    throw error;
  }
});

export const paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_LIMIT).default(DEFAULT_PAGE_LIMIT),
  cursor: objectIdSchema.optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const reasonBodySchema = z.object({ reason: text(3, 500) });
export type ReasonBody = z.infer<typeof reasonBodySchema>;
